import { useState, useEffect } from 'react'
import type { ChangeEvent } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { Modal } from 'antd'
import { Check, Loader2, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  fetchHardQuestionTags,
  assignHardQuestionTag,
} from '@/api/feedback-conversation.api'
import type { HardQuestion } from '@/api/feedback-conversation.api'

interface AssignLabelModalProps {
  isOpen: boolean
  record: HardQuestion | null
  onClose: () => void
  onSuccess: () => void
}

export function AssignLabelModal({
  isOpen,
  record,
  onClose,
  onSuccess,
}: AssignLabelModalProps) {
  const [labelInput, setLabelInput] = useState('')

  const { data: popularTags = [] } = useQuery({
    queryKey: ['hard-question-tags'],
    queryFn: ({ signal }) => fetchHardQuestionTags(signal),
    enabled: isOpen,
  })

  const mutation = useMutation({
    mutationFn: (tagName: string) => {
      if (!record?.id) throw new Error('Không tìm thấy ID câu hỏi')
      return assignHardQuestionTag(record.id, tagName)
    },
    onSuccess: () => {
      onSuccess()
    },
  })

  useEffect(() => {
    if (isOpen) {
      setLabelInput('')
    }
  }, [isOpen])

  const handleSelectPopular = (name: string) => {
    setLabelInput(name)
  }

  const handleSubmit = (e?: React.SyntheticEvent) => {
    if (e) e.preventDefault()
    const trimmedInput = labelInput.trim()
    if (!trimmedInput) return
    const normalizedInput = trimmedInput.replace(/\s+/g, ' ').toLowerCase()
    const matchedTag = popularTags.find(
      (t) => t.label.toLowerCase() === normalizedInput,
    )

    const payload = matchedTag
      ? matchedTag.label
      : popularTags.find((t) => t.name === 'OTHER')?.label || 'Khác'

    mutation.mutate(payload)
  }

  return (
    <Modal
      open={isOpen}
      onCancel={onClose}
      footer={null}
      closable={false}
      centered
      width={700}
      destroyOnHidden
      className="[&_.ant-modal-container]:bg-white [&_.ant-modal-content]:!p-0 [&_.ant-modal-content]:!rounded-3xl shadow-xl max-w-[95vw]"
    >
      <div className="flex flex-col">
        <div className="flex items-center justify-between pb-4">
          <h2 className="!m-0 text-[#1E2F41] text-xl font-semibold">
            Gán nhãn nội dung
          </h2>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            className="hover:bg-gray-100 h-8 w-8 rounded-full"
          >
            <X size={20} />
          </Button>
        </div>

        <div className="space-y-5">
          <div className="rounded-[1.25rem] border border-solid border-[#1E2F4140] bg-[#1E2F410D] p-5 text-[15px] text-[#1E2F41] max-h-48 overflow-y-auto">
            {record?.question || 'Nội dung trống...'}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 pb-3">
            <div className="space-y-2">
              <label htmlFor="assign-label-input" className="text-[#1E2F41]">
                Nhãn <span className="text-red-500">*</span>
              </label>
              <Input
                id="assign-label-input"
                placeholder="Nhập nhãn"
                value={labelInput}
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  setLabelInput(e.target.value)
                }
                className="rounded-full h-11 !bg-white"
                disabled={mutation.isPending}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              <span className="text-[#1E2F41B2] font-medium">Phổ biến:</span>
              {popularTags.map((tag) => {
                const trimmedInput = labelInput.trim()
                const normalizedInput = trimmedInput
                  .replace(/\s+/g, ' ')
                  .toLowerCase()
                const isCustomInput =
                  trimmedInput.length > 0 &&
                  !popularTags.some(
                    (t) => t.label.toLowerCase() === normalizedInput,
                  )
                const isSelected =
                  tag.name === 'OTHER'
                    ? normalizedInput === tag.label.toLowerCase() ||
                      isCustomInput
                    : normalizedInput === tag.label.toLowerCase()

                return (
                  <Button
                    key={tag.name}
                    onClick={() => handleSelectPopular(tag.label)}
                    disabled={mutation.isPending}
                    className={`flex items-center gap-1.5 rounded-full px-4 py-1.5 s ${
                      isSelected
                        ? 'border-[#CE7A58] text-[#CE7A58] bg-white'
                        : 'border-[#1E2F4140] text-[#1E2F41] bg-white hover:!bg-white hover:!border-[#CE7A58] hover:!text-[#CE7A58]'
                    }`}
                  >
                    {tag.label}
                    {isSelected && (
                      <span className="flex items-center justify-center bg-[#CE7A58] text-white rounded-full w-4 h-4">
                        <Check size={10} strokeWidth={3} />
                      </span>
                    )}
                  </Button>
                )
              })}
            </div>
          </form>
        </div>

        <div className=" border border-t-solid border-[#1E2F410D] px-6 py-3 flex justify-center gap-4">
          <Button
            variant="outline"
            size="md"
            onClick={onClose}
            disabled={mutation.isPending}
            className="min-w-[160px] h-10"
          >
            Đóng
          </Button>
          <Button
            variant="primary"
            size="md"
            onClick={handleSubmit}
            disabled={!labelInput.trim() || mutation.isPending}
            className="min-w-[160px] h-10"
          >
            {mutation.isPending ? (
              <Loader2 className="animate-spin mr-2" size={16} />
            ) : null}
            Xác nhận
          </Button>
        </div>
      </div>
    </Modal>
  )
}
